import { useState, useRef, useEffect } from 'react'
import { Card, Table, Button, Badge, Form, Row, Col, Spinner, Alert, Modal, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { formatCurrency } from '../../utils/formatters'

// Inferir origen de la transacción por el prefijo de reference (sin XMLCFDI)
const getOrigenFromReference = (reference) => {
  if (!reference || !reference.trim()) return 'Manual'
  const ref = reference.trim()
  if (ref.startsWith('INV-')) return 'Factura'
  if (ref.startsWith('BILL-PAY-')) return 'Pago Fact. Recibida'
  if (ref.startsWith('BILL-')) return 'Factura Recibida'
  if (ref.startsWith('PAY-') || ref.startsWith('APL-')) return 'Pago'
  if (ref.startsWith('REV-')) return 'Reverso'
  return 'Manual'
}
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useClientFilter } from '../../hooks/useClientFilter'
import api from '../../services/api'
import { formatDateOnlyEsMX } from '../../utils/apiDateFormat'

const TransactionsList = ({ 
  initialFilters = {},
  initialSortField = 'date',
  initialSortDirection = 'desc'
}) => {
  const { user } = useAuth()
  const { selectedClientId, isFiltering } = useClientFilter()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState({
    account_id: initialFilters.account_id || '',
    client_id: initialFilters.client_id || '',
    start_date: initialFilters.start_date || '',
    end_date: initialFilters.end_date || '',
    is_posted: initialFilters.is_posted || 'false', // Por defecto pendientes
    is_deductible: initialFilters.is_deductible || '',
    account_type: initialFilters.account_type || '',
    cost_center_id: initialFilters.cost_center_id || ''
  })
  const [sortField, setSortField] = useState(initialSortField)
  const [sortDirection, setSortDirection] = useState(initialSortDirection)
  
  // Ref para almacenar los valores anteriores de initialFilters y evitar loops infinitos
  const prevInitialFiltersRef = useRef(null)
  const isInitialMount = useRef(true)
  
  // Actualizar filtros cuando cambien los props iniciales (solo si realmente cambiaron)
  useEffect(() => {
    const currentFilters = initialFilters || {}
    
    // En el primer render, solo inicializar la referencia
    if (isInitialMount.current) {
      isInitialMount.current = false
      prevInitialFiltersRef.current = { ...currentFilters }
      return
    }
    
    // Comparar valores individuales para detectar cambios reales
    const prevFilters = prevInitialFiltersRef.current || {}
    const hasChanges = Object.keys(currentFilters).some(key => 
      currentFilters[key] !== prevFilters[key] && currentFilters[key] !== undefined && currentFilters[key] !== ''
    )
    
    // Solo actualizar si hay cambios reales
    if (hasChanges) {
      prevInitialFiltersRef.current = { ...currentFilters }
      const isPosted = currentFilters.is_posted ?? prevFilters.is_posted ?? 'false'
      setViewMode(isPosted === 'true' ? 'contabilizadas' : 'pendientes')
      setFilters(prev => {
        const newFilters = {
          ...prev,
          ...Object.fromEntries(
            Object.entries(currentFilters).filter(([_, v]) => v !== undefined && v !== '')
          )
        }
        return newFilters
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialFilters?.client_id,
    initialFilters?.account_id,
    initialFilters?.account_type,
    initialFilters?.start_date,
    initialFilters?.end_date,
    initialFilters?.is_posted,
    initialFilters?.is_deductible,
    initialFilters?.cost_center_id
  ])
  
  const [selectedTransactions, setSelectedTransactions] = useState([])
  const [exporting, setExporting] = useState(false)
  // Vista: 'pendientes' (por defecto) o 'contabilizadas'
  const [viewMode, setViewMode] = useState('pendientes')

  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      client_id: selectedClientId != null ? String(selectedClientId) : '',
    }))
  }, [selectedClientId])
  
  // Estados para modal de búsqueda de cuentas
  const [mostrarModalCuenta, setMostrarModalCuenta] = useState(false)
  const [busquedaModalCuenta, setBusquedaModalCuenta] = useState('')
  const [resultadosModalCuenta, setResultadosModalCuenta] = useState([])
  const [buscandoModalCuenta, setBuscandoModalCuenta] = useState(false)
  const debounceTimeoutCuentaRef = useRef(null)
  const [selectedAccountInfo, setSelectedAccountInfo] = useState(null)

  // Obtener clientes para el filtro
  const { data: clients } = useQuery(
    'clients',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Cargar información de la cuenta seleccionada cuando cambia filters.account_id
  useQuery(
    ['account-info', filters.account_id],
    async () => {
      if (!filters.account_id) {
        setSelectedAccountInfo(null)
        return null
      }
      try {
        const response = await api.get(`/accounting/accounts/${filters.account_id}/`)
        setSelectedAccountInfo(response.data)
        return response.data
      } catch (error) {
        console.error('Error cargando información de cuenta:', error)
        setSelectedAccountInfo(null)
        return null
      }
    },
    { enabled: !!filters.account_id }
  )

  // Obtener centros de costo para el filtro
  const { data: costCenters } = useQuery(
    'cost-centers',
    async () => {
      const response = await api.get('/accounting/cost-centers/')
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Construir query params
  const buildQueryParams = () => {
    const params = new URLSearchParams()
    if (filters.account_id) params.append('account_id', filters.account_id)
    if (filters.client_id) params.append('client_id', filters.client_id)
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)
    if (filters.is_posted !== '') params.append('is_posted', filters.is_posted)
    if (filters.is_deductible !== '') params.append('is_deductible', filters.is_deductible)
    if (filters.account_type) params.append('account_type', filters.account_type)
    if (filters.cost_center_id) params.append('cost_center_id', filters.cost_center_id)
    if (sortField) params.append('ordering', sortDirection === 'desc' ? `-${sortField}` : sortField)
    params.append('page_size', '200') // Más filas para contabilizar masivo sin cambiar de página
    return params.toString()
  }

  // Obtener transacciones
  const { data: transactionsData, isLoading, error } = useQuery(
    ['transactions', filters, sortField, sortDirection],
    async () => {
      const queryParams = buildQueryParams()
      const url = `/accounting/transactions/${queryParams ? `?${queryParams}` : ''}`
      const response = await api.get(url)
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Obtener indicadores (siempre visibles encima de filtros)
  const filtersForIndicators = { ...filters, is_posted: '' }
  const { data: indicatorsData } = useQuery(
    ['transactions-indicators', filtersForIndicators],
    async () => {
      const params = new URLSearchParams()
      if (filters.account_id) params.append('account_id', filters.account_id)
      if (filters.client_id) params.append('client_id', filters.client_id)
      if (filters.start_date) params.append('start_date', filters.start_date)
      if (filters.end_date) params.append('end_date', filters.end_date)
      if (filters.is_deductible !== '') params.append('is_deductible', filters.is_deductible)
      if (filters.account_type) params.append('account_type', filters.account_type)
      if (filters.cost_center_id) params.append('cost_center_id', filters.cost_center_id)
      const qs = params.toString()
      const response = await api.get(`/accounting/transactions/indicators/${qs ? `?${qs}` : ''}`)
      return response.data
    },
    { enabled: !!user }
  )

  // Obtener resumen de transacciones (siempre visible)
  const { data: summaryData, isLoading: isLoadingSummary } = useQuery(
    ['transactions-summary', filters],
    async () => {
      const queryParams = buildQueryParams()
      const url = `/accounting/transactions/summary/${queryParams ? `?${queryParams}` : ''}`
      const response = await api.get(url)
      return response.data
    },
    { enabled: !!user }
  )

  const transactions = transactionsData || []

  // Mutación para eliminar transacción
  const deleteMutation = useMutation(
    async (transactionId) => {
      return api.delete(`/accounting/transactions/${transactionId}/`)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('transactions')
        toast.success('Transacción eliminada exitosamente')
      },
      onError: (error) => {
        const message = error.response?.data?.error || error.response?.data?.detail || 'Error al eliminar transacción'
        toast.error(message)
        console.error('Error al eliminar transacción:', error.response?.data)
      }
    }
  )

  // Mutación para contabilizar transacción individual
  const contabilizarMutation = useMutation(
    async (transactionId) => {
      return api.post(`/accounting/transactions/${transactionId}/contabilizar/`)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('transactions')
        toast.success('Transacción contabilizada exitosamente')
        setSelectedTransactions([])
      },
      onError: (error) => {
        const message = error.response?.data?.error || error.response?.data?.detail || 'Error al contabilizar transacción'
        toast.error(message)
        console.error('Error al contabilizar transacción:', error.response?.data)
      }
    }
  )

  // Mutación para revertir contabilización
  const revertirMutation = useMutation(
    async (transactionId) => {
      return api.post(`/accounting/transactions/${transactionId}/revertir/`)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('transactions')
        queryClient.invalidateQueries('transactions-summary')
        toast.success('Transacción revertida exitosamente')
        setSelectedTransactions([])
      },
      onError: (error) => {
        const message = error.response?.data?.error || error.response?.data?.detail || 'Error al revertir transacción'
        toast.error(message)
      }
    }
  )

  // Mutación para contabilizar masivamente
  const contabilizarMasivoMutation = useMutation(
    async (transactionIds) => {
      return api.post('/accounting/transactions/contabilizar_masivo/', {
        transaction_ids: transactionIds
      })
    },
    {
      onSuccess: (response) => {
        const data = response.data
        queryClient.invalidateQueries('transactions')
        queryClient.invalidateQueries('transactions-summary')
        queryClient.invalidateQueries('transactions-indicators')
        toast.success(data?.message || `${data?.updated_count || 0} transacción(es) contabilizada(s) exitosamente`)
        if (data?.errors?.length) {
          data.errors.forEach((err) => toast.warning(err))
        }
        setSelectedTransactions([])
      },
      onError: (error) => {
        const data = error.response?.data
        const message = (typeof data?.error === 'string' ? data.error : null) || data?.detail || 'Error al contabilizar transacciones'
        toast.error(message)
        if (process.env.NODE_ENV === 'development') {
          console.error('Error contabilizar masivo:', { status: error.response?.status, data })
        }
      }
    }
  )

  // Mutación para revertir contabilización masivamente
  const revertirMasivoMutation = useMutation(
    async (transactionIds) => {
      return api.post('/accounting/transactions/revertir_masivo/', {
        transaction_ids: transactionIds
      })
    },
    {
      onSuccess: (response) => {
        const data = response.data
        queryClient.invalidateQueries('transactions')
        queryClient.invalidateQueries('transactions-summary')
        queryClient.invalidateQueries('transactions-indicators')
        if (data?.updated_count > 0) {
          toast.success(data?.message || `${data.updated_count} transacción(es) revertida(s) exitosamente`)
        } else {
          toast.warning(data?.message || 'No se pudo revertir ninguna transacción')
        }
        if (data?.errors?.length) {
          data.errors.forEach((err) => toast.warning(err))
        }
        setSelectedTransactions([])
      },
      onError: (error) => {
        const data = error.response?.data
        const message = (typeof data?.error === 'string' ? data.error : null) || data?.detail || 'Error al revertir transacciones'
        toast.error(message)
      }
    }
  )

  const handleDelete = (transaction) => {
    if (window.confirm(`¿Estás seguro de eliminar esta transacción?\n\nCuenta: ${transaction.account_code}\nDescripción: ${transaction.description?.substring(0, 50) || 'N/A'}\nMonto: ${formatCurrency(transaction.debit || transaction.credit || 0)}`)) {
      deleteMutation.mutate(transaction.id)
    }
  }

  const handleContabilizar = (transaction) => {
    if (window.confirm(`¿Estás seguro de contabilizar esta transacción?\n\nCuenta: ${transaction.account_code}\nDescripción: ${transaction.description?.substring(0, 50) || 'N/A'}\nMonto: ${formatCurrency(transaction.debit || transaction.credit || 0)}`)) {
      contabilizarMutation.mutate(transaction.id)
    }
  }

  const handleRevertir = (transaction) => {
    if (window.confirm(`¿Estás seguro de revertir la contabilización de esta transacción?\n\nCuenta: ${transaction.account_code}\nDescripción: ${transaction.description?.substring(0, 50) || 'N/A'}\nLa transacción volverá a estado "Pendiente".`)) {
      revertirMutation.mutate(transaction.id)
    }
  }

  const handleToggleSelect = (transactionId) => {
    setSelectedTransactions(prev => {
      if (prev.includes(transactionId)) {
        return prev.filter(id => id !== transactionId)
      } else {
        return [...prev, transactionId]
      }
    })
  }

  const getSelectableIds = () => {
    if (viewMode === 'contabilizadas') {
      return transactions.filter((t) => t.is_posted).map((t) => t.id)
    }
    return transactions.filter((t) => !t.is_posted).map((t) => t.id)
  }

  const handleSelectAll = () => {
    const selectable = getSelectableIds()
    const allSelected = selectable.length > 0 && selectable.every((id) => selectedTransactions.includes(id))
    if (allSelected) {
      setSelectedTransactions([])
    } else {
      setSelectedTransactions(selectable)
    }
  }

  const handleContabilizarSeleccionadas = () => {
    if (selectedTransactions.length === 0) {
      toast.warning('Selecciona al menos una transacción para contabilizar')
      return
    }

    const pendientesIds = selectedTransactions.filter((id) => {
      const t = transactions.find((tr) => tr.id === id)
      return t && !t.is_posted
    })
    const omitidas = selectedTransactions.length - pendientesIds.length
    if (omitidas > 0) {
      toast.warning(`Se omitieron ${omitidas} transacción(es) ya contabilizadas de la selección`)
    }
    if (pendientesIds.length === 0) {
      toast.warning('Selecciona al menos una transacción pendiente para contabilizar')
      return
    }

    if (window.confirm(`¿Contabilizar ${pendientesIds.length} transacción(es) seleccionada(s)?`)) {
      contabilizarMasivoMutation.mutate([...pendientesIds])
    }
  }

  const handleRevertirSeleccionadas = () => {
    if (selectedTransactions.length === 0) {
      toast.warning('Selecciona al menos una transacción para revertir')
      return
    }

    const contabilizadasIds = selectedTransactions.filter((id) => {
      const t = transactions.find((tr) => tr.id === id)
      return t && t.is_posted
    })
    const omitidas = selectedTransactions.length - contabilizadasIds.length
    if (omitidas > 0) {
      toast.warning(`Se omitieron ${omitidas} transacción(es) no contabilizadas de la selección`)
    }
    if (contabilizadasIds.length === 0) {
      toast.warning('Selecciona al menos una transacción contabilizada para revertir')
      return
    }

    if (
      window.confirm(
        `¿Revertir la contabilización de ${contabilizadasIds.length} transacción(es) seleccionada(s)? Volverán a estado Pendiente.`
      )
    ) {
      revertirMasivoMutation.mutate([...contabilizadasIds])
    }
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up text-muted"></i>
    }
    return sortDirection === 'asc' 
      ? <i className="bi bi-arrow-up text-primary"></i>
      : <i className="bi bi-arrow-down text-primary"></i>
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const next = { ...prev, [field]: value }
      if (field === 'is_posted') {
        setViewMode(value === 'true' ? 'contabilizadas' : 'pendientes')
        setSelectedTransactions([])
      }
      return next
    })
  }

  const handleViewModeChange = (mode) => {
    setViewMode(mode)
    setSelectedTransactions([])
    setFilters(prev => ({ ...prev, is_posted: mode === 'contabilizadas' ? 'true' : 'false' }))
  }

  const clearFilters = () => {
    setFilters({
      account_id: '',
      client_id: '',
      start_date: '',
      end_date: '',
      is_posted: 'false',
      is_deductible: '',
      account_type: '',
      cost_center_id: ''
    })
    setViewMode('pendientes')
  }

  // Abrir modal de búsqueda de cuentas
  const abrirModalCuenta = () => {
    setMostrarModalCuenta(true)
    if (selectedAccountInfo?.code) {
      setBusquedaModalCuenta(selectedAccountInfo.code)
      buscarCuentasModal(selectedAccountInfo.code)
    }
  }

  // Cerrar modal de búsqueda de cuentas
  const cerrarModalCuenta = () => {
    setMostrarModalCuenta(false)
    setBusquedaModalCuenta('')
    setResultadosModalCuenta([])
  }

  // Buscar cuentas contables en el modal
  const buscarCuentasModal = async (query) => {
    if (!query || query.trim().length < 2) {
      setResultadosModalCuenta([])
      return
    }

    // Limpiar timeout anterior
    if (debounceTimeoutCuentaRef.current) {
      clearTimeout(debounceTimeoutCuentaRef.current)
    }

    // Debounce de 300ms
    debounceTimeoutCuentaRef.current = setTimeout(async () => {
      try {
        setBuscandoModalCuenta(true)
        const params = new URLSearchParams({
          search: query.trim(),
          is_active: 'true',
          page_size: '200'
        })
        if (filters.client_id) {
          params.append('cliente_id', filters.client_id)
        }

        const response = await api.get(`/accounting/accounts/?${params.toString()}`)
        const results = response.data.results || response.data || []
        setResultadosModalCuenta(results)
      } catch (error) {
        console.error('Error buscando cuentas contables:', error)
        setResultadosModalCuenta([])
      } finally {
        setBuscandoModalCuenta(false)
      }
    }, 300)
  }

  // Seleccionar cuenta contable del modal
  const seleccionarCuenta = (cuenta) => {
    if (!cuenta || !cuenta.id) {
      return
    }
    
    handleFilterChange('account_id', cuenta.id.toString())
    setSelectedAccountInfo({
      id: cuenta.id,
      code: cuenta.code,
      name: cuenta.name,
      account_type: cuenta.account_type
    })
    cerrarModalCuenta()
  }

  const handleExport = async (format) => {
    try {
      setExporting(true)
      const queryParams = buildQueryParams()
      const url = `/accounting/export-transactions/?${queryParams ? `${queryParams}&` : ''}format=${format}`
      
      const response = await api.get(url, {
        responseType: 'blob'
      })
      
      const url_blob = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url_blob
      link.setAttribute('download', `Transacciones_Contables.${format === 'excel' ? 'xlsx' : 'pdf'}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success(`Exportación ${format.toUpperCase()} completada`)
    } catch (error) {
      console.error('Error exportando:', error)
      toast.error('Error al exportar transacciones')
    } finally {
      setExporting(false)
    }
  }

  // Calcular totales
  const totalDebit = transactions.reduce((sum, t) => sum + parseFloat(t.debit || 0), 0)
  const totalCredit = transactions.reduce((sum, t) => sum + parseFloat(t.credit || 0), 0)
  const totalBalance = totalDebit - totalCredit

  if (error) {
    return (
      <Alert variant="danger">
        Error al cargar transacciones: {error.message}
      </Alert>
    )
  }

  const selectableIds = getSelectableIds()
  const selectedPendientes = selectedTransactions.filter(id => {
    const transaction = transactions.find(t => t.id === id)
    return transaction && !transaction.is_posted
  }).length
  const selectedContabilizadas = selectedTransactions.filter(id => {
    const transaction = transactions.find(t => t.id === id)
    return transaction && transaction.is_posted
  }).length
  const allSelectableSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedTransactions.includes(id))

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Transacciones Contables</h3>
        <div className="d-flex gap-2">
          <Button
            variant="outline-danger"
            onClick={() => handleExport('pdf')}
            disabled={exporting || transactions.length === 0}
          >
            {exporting ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-file-pdf me-1"></i> PDF</>}
          </Button>
          <Button
            variant="outline-success"
            onClick={() => handleExport('excel')}
            disabled={exporting || transactions.length === 0}
          >
            {exporting ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-file-excel me-1"></i> Excel</>}
          </Button>
          {selectedPendientes > 0 && (
            <Button
              variant="success"
              onClick={handleContabilizarSeleccionadas}
              disabled={contabilizarMasivoMutation.isLoading}
            >
              <i className="bi bi-check-circle me-1"></i>
              Contabilizar {selectedPendientes} Seleccionada(s)
            </Button>
          )}
          {selectedContabilizadas > 0 && (
            <Button
              variant="warning"
              onClick={handleRevertirSeleccionadas}
              disabled={revertirMasivoMutation.isLoading}
            >
              <i className="bi bi-arrow-counterclockwise me-1"></i>
              Revertir contabilización {selectedContabilizadas} Seleccionada(s)
            </Button>
          )}
        </div>
      </div>

      {/* Indicadores - siempre visibles encima de filtros */}
      {indicatorsData && (
        <Card className="mb-3">
          <Card.Body className="py-2">
            <Row className="g-2">
              <Col xs={6} md={2}>
                <div className="text-center p-2 bg-warning bg-opacity-25 rounded">
                  <small className="text-muted d-block">Pendientes</small>
                  <strong>{indicatorsData.pendientes_count ?? 0}</strong>
                </div>
              </Col>
              <Col xs={6} md={2}>
                <div className="text-center p-2 bg-success bg-opacity-25 rounded">
                  <small className="text-muted d-block">Contabilizadas</small>
                  <strong>{indicatorsData.contabilizadas_count ?? 0}</strong>
                </div>
              </Col>
              <Col xs={6} md={2}>
                <div className="text-center p-2 bg-danger bg-opacity-25 rounded">
                  <small className="text-muted d-block">Débitos</small>
                  <strong>
                    {formatCurrency(indicatorsData.total_debit || 0)}
                  </strong>
                </div>
              </Col>
              <Col xs={6} md={2}>
                <div className="text-center p-2 bg-success bg-opacity-25 rounded">
                  <small className="text-muted d-block">Créditos</small>
                  <strong>
                    {formatCurrency(indicatorsData.total_credit || 0)}
                  </strong>
                </div>
              </Col>
              <Col xs={6} md={2}>
                <div className="text-center p-2 bg-primary bg-opacity-25 rounded">
                  <small className="text-muted d-block">Diferencia</small>
                  <strong>
                    {formatCurrency(indicatorsData.diferencia || 0)}
                  </strong>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Filtros */}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span>Filtros</span>
            <div className="d-flex align-items-center gap-2">
              <div className="btn-group" role="group">
                <Button
                  variant={viewMode === 'pendientes' ? 'warning' : 'outline-warning'}
                  size="sm"
                  onClick={() => handleViewModeChange('pendientes')}
                >
                  Pendientes
                </Button>
                <Button
                  variant={viewMode === 'contabilizadas' ? 'success' : 'outline-success'}
                  size="sm"
                  onClick={() => handleViewModeChange('contabilizadas')}
                >
                  Contabilizadas
                </Button>
              </div>
              <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                Limpiar Filtros
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Cuenta</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    value={selectedAccountInfo ? `${selectedAccountInfo.code} - ${selectedAccountInfo.name}` : ''}
                    placeholder="Seleccione una cuenta contable"
                    readOnly
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="outline-primary"
                    onClick={abrirModalCuenta}
                    type="button"
                  >
                    <i className="bi bi-search"></i> Buscar
                  </Button>
                  {filters.account_id && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        handleFilterChange('account_id', '')
                        setSelectedAccountInfo(null)
                      }}
                      type="button"
                      title="Limpiar cuenta"
                    >
                      <i className="bi bi-x"></i>
                    </Button>
                  )}
                </div>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Cliente</Form.Label>
                <Form.Select
                  value={filters.client_id}
                  onChange={(e) => handleFilterChange('client_id', e.target.value)}
                  disabled={isFiltering}
                >
                  <option value="">Todos los clientes</option>
                  {clients?.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.razon_social || client.business_name || `${client.nombre || ''} ${client.apellido_paterno || ''}`.trim()}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Fecha Desde</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Fecha Hasta</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={1}>
              <Form.Group className="mb-3">
                <Form.Label>Contabilizado</Form.Label>
                <Form.Select
                  value={filters.is_posted}
                  onChange={(e) => handleFilterChange('is_posted', e.target.value)}
                >
                  <option value="false">Pendientes</option>
                  <option value="true">Contabilizadas</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={1}>
              <Form.Group className="mb-3">
                <Form.Label>Deducible</Form.Label>
                <Form.Select
                  value={filters.is_deductible}
                  onChange={(e) => handleFilterChange('is_deductible', e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="true">Sí</option>
                  <option value="false">No</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Tipo de Cuenta</Form.Label>
                <Form.Select
                  value={filters.account_type}
                  onChange={(e) => handleFilterChange('account_type', e.target.value)}
                >
                  <option value="">Todos los tipos</option>
                  <option value="ACTIVO">Activo</option>
                  <option value="PASIVO">Pasivo</option>
                  <option value="CAPITAL">Capital</option>
                  <option value="INGRESO">Ingreso</option>
                  <option value="GASTO">Gasto</option>
                  <option value="COSTO">Costo</option>
                  <option value="RESULTADO_FINANCIAMIENTO">Resultado financiamiento</option>
                  <option value="CUENTAS_ORDEN">Cuentas de orden</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Centro de Costo</Form.Label>
                <Form.Select
                  value={filters.cost_center_id}
                  onChange={(e) => handleFilterChange('cost_center_id', e.target.value)}
                >
                  <option value="">Todos los centros</option>
                  {costCenters?.map(cc => (
                    <option key={cc.id} value={cc.id}>
                      {cc.code} - {cc.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Resumen de transacciones - siempre visible */}
      {summaryData && (
        <Card className="mb-4">
          <Card.Header>
            <strong>Resumen de Transacciones</strong>
          </Card.Header>
          <Card.Body>
            {isLoadingSummary ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : (
              <Row>
                <Col md={3}>
                  <div className="text-center p-3 bg-light rounded">
                    <div className="text-muted small">Total Débitos</div>
                    <div className="h4 text-danger mb-0">
                      {formatCurrency(summaryData.totales?.total_debit || 0)}
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center p-3 bg-light rounded">
                    <div className="text-muted small">Total Créditos</div>
                    <div className="h4 text-success mb-0">
                      {formatCurrency(summaryData.totales?.total_credit || 0)}
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center p-3 bg-light rounded">
                    <div className="text-muted small">Saldo Neto</div>
                    <div className={`h4 mb-0 ${(summaryData.totales?.saldo_neto || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                      {formatCurrency(summaryData.totales?.saldo_neto || 0)}
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center p-3 bg-light rounded">
                    <div className="text-muted small">Total Transacciones</div>
                    <div className="h4 text-primary mb-0">
                      {summaryData.totales?.total_transactions || 0}
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Tabla de transacciones */}
      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : transactions.length > 0 ? (
        <Card>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <Form.Check
                        type="checkbox"
                        checked={allSelectableSelected}
                        onChange={handleSelectAll}
                        disabled={selectableIds.length === 0}
                        title={
                          viewMode === 'contabilizadas'
                            ? 'Seleccionar todas las transacciones contabilizadas visibles'
                            : 'Seleccionar todas las transacciones pendientes visibles'
                        }
                      />
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('date')}
                      className="position-relative"
                    >
                      <span className="d-inline-flex align-items-center gap-1">
                        Fecha
                        {getSortIcon('date')}
                      </span>
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('account__code')}
                      className="position-relative"
                    >
                      <span className="d-inline-flex align-items-center gap-1">
                        Cuenta
                        {getSortIcon('account__code')}
                      </span>
                    </th>
                    <th>Cliente</th>
                    <th>RFC Emisor</th>
                    <th>RFC Receptor</th>
                    <th>Descripción</th>
                    <th>Referencia</th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('debit')}
                      className="position-relative text-end"
                    >
                      <span className="d-inline-flex align-items-center gap-1">
                        Débito
                        {getSortIcon('debit')}
                      </span>
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('credit')}
                      className="position-relative text-end"
                    >
                      <span className="d-inline-flex align-items-center gap-1">
                        Crédito
                        {getSortIcon('credit')}
                      </span>
                    </th>
                    <th>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-estado">
                            <strong>Estado:</strong> Contabilizado/Pendiente indica si la transacción ya fue registrada en contabilidad.
                            <br />
                            <strong>Deducible:</strong> Aparece cuando la transacción cumple reglas de deducibilidad fiscal del cliente (gastos deducibles para efectos de impuestos).
                          </Tooltip>
                        }
                      >
                        <span style={{ cursor: 'help', borderBottom: '1px dotted' }}>Estado</span>
                      </OverlayTrigger>
                    </th>
                    <th>Origen</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(transaction => (
                    <tr key={transaction.id}>
                      <td>
                        {((viewMode === 'pendientes' && !transaction.is_posted) ||
                          (viewMode === 'contabilizadas' && transaction.is_posted)) && (
                          <Form.Check
                            type="checkbox"
                            checked={selectedTransactions.includes(transaction.id)}
                            onChange={() => handleToggleSelect(transaction.id)}
                            title={
                              viewMode === 'contabilizadas'
                                ? 'Seleccionar para revertir contabilización'
                                : 'Seleccionar para contabilizar'
                            }
                          />
                        )}
                        {((viewMode === 'pendientes' && transaction.is_posted) ||
                          (viewMode === 'contabilizadas' && !transaction.is_posted)) && (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>{formatDateOnlyEsMX(transaction.date, {}, '—')}</td>
                      <td>
                        <div>
                          <strong>{transaction.account_code}</strong>
                        </div>
                        <small className="text-muted">{transaction.account_name}</small>
                      </td>
                      <td>{transaction.client_name || 'N/A'}</td>
                      <td>{transaction.emisor_rfc || 'N/A'}</td>
                      <td>{transaction.receptor_rfc || 'N/A'}</td>
                      <td>{transaction.description || 'N/A'}</td>
                      <td>{transaction.reference || 'N/A'}</td>
                      <td className="text-end">
                        {parseFloat(transaction.debit || 0) > 0 ? (
                          <span className="text-danger">
                            {formatCurrency(transaction.debit)}
                          </span>
                        ) : (
                          <span className="text-muted">$0.00</span>
                        )}
                      </td>
                      <td className="text-end">
                        {parseFloat(transaction.credit || 0) > 0 ? (
                          <span className="text-success">
                            {formatCurrency(transaction.credit)}
                          </span>
                        ) : (
                          <span className="text-muted">$0.00</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          {transaction.is_posted ? (
                            <Badge bg="success">Contabilizado</Badge>
                          ) : (
                            <Badge bg="warning">Pendiente</Badge>
                          )}
                          {transaction.is_deductible && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-deducible-${transaction.id}`}>
                                  Esta transacción es deducible para efectos fiscales según las reglas del cliente.
                                </Tooltip>
                              }
                            >
                              <Badge bg="info" style={{ cursor: 'help' }}>Deducible</Badge>
                            </OverlayTrigger>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge bg="light" text="dark">
                          {getOrigenFromReference(transaction.reference)}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex gap-2 flex-wrap">
                          {!transaction.is_posted && (
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleContabilizar(transaction)}
                              disabled={contabilizarMutation.isLoading}
                              title="Contabilizar transacción"
                              style={{ minWidth: '120px' }}
                            >
                              <i className="bi bi-check-circle me-1"></i>
                              <span>Contabilizar</span>
                            </Button>
                          )}
                          {transaction.is_posted && (
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => handleRevertir(transaction)}
                              disabled={revertirMutation.isLoading}
                              title="Revertir contabilización (individual)"
                              style={{ minWidth: '120px' }}
                            >
                              <i className="bi bi-arrow-counterclockwise me-1"></i>
                              <span>Revertir</span>
                            </Button>
                          )}
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(transaction)}
                            disabled={deleteMutation.isLoading || transaction.is_posted}
                            title={
                              transaction.is_posted 
                                ? "No se puede eliminar: la transacción está contabilizada" 
                                : "Eliminar transacción"
                            }
                            style={{ minWidth: '90px' }}
                          >
                            <i className="bi bi-trash me-1"></i>
                            <span>Eliminar</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="table-info">
                    <td colSpan="6" className="text-end"><strong>Totales:</strong></td>
                    <td className="text-end">
                      <strong className="text-danger">
                        {formatCurrency(totalDebit)}
                      </strong>
                    </td>
                    <td className="text-end">
                      <strong className="text-success">
                        {formatCurrency(totalCredit)}
                      </strong>
                    </td>
                    <td className="text-end">
                      <strong>
                        {formatCurrency(totalBalance)}
                      </strong>
                    </td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Alert variant="info">
          No hay transacciones contables registradas.
        </Alert>
      )}

      {/* Modal de búsqueda de cuentas contables */}
      <Modal
        show={mostrarModalCuenta}
        onHide={cerrarModalCuenta}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Buscar Cuenta Contable</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Buscar por código o nombre</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Escribe para buscar (mínimo 2 caracteres)..."
                value={busquedaModalCuenta}
                onChange={(e) => {
                  setBusquedaModalCuenta(e.target.value)
                  buscarCuentasModal(e.target.value)
                }}
                autoFocus
              />
              {buscandoModalCuenta && (
                <InputGroup.Text>
                  <Spinner animation="border" size="sm" />
                </InputGroup.Text>
              )}
            </InputGroup>
            <Form.Text className="text-muted">
              Busca por código de cuenta o por nombre
            </Form.Text>
          </Form.Group>

          {resultadosModalCuenta.length > 0 ? (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '120px' }}>Código</th>
                    <th>Nombre</th>
                    <th style={{ minWidth: '180px' }}>Ruta completa</th>
                    <th style={{ width: '100px' }}>Tipo</th>
                    <th style={{ width: '90px' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {resultadosModalCuenta.map((cuenta) => (
                    <tr key={cuenta.id}>
                      <td><strong>{cuenta.code}</strong></td>
                      <td>
                        <div style={{ fontSize: '0.9rem' }}>
                          {cuenta.name}
                        </div>
                        {cuenta.description && (
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {cuenta.description.substring(0, 50)}...
                          </div>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">{cuenta.full_path || cuenta.code}</small>
                      </td>
                      <td>
                        {cuenta.account_type && (
                          <Badge bg="info" style={{ fontSize: '0.75rem' }}>
                            {cuenta.account_type}
                          </Badge>
                        )}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => seleccionarCuenta(cuenta)}
                        >
                          Seleccionar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : busquedaModalCuenta.trim().length >= 2 && !buscandoModalCuenta ? (
            <Alert variant="info" className="mb-0">
              No se encontraron resultados para &quot;{busquedaModalCuenta}&quot;
            </Alert>
          ) : busquedaModalCuenta.trim().length < 2 ? (
            <Alert variant="secondary" className="mb-0">
              Escribe al menos 2 caracteres para comenzar la búsqueda
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cerrarModalCuenta}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default TransactionsList
